/**
 * useGetPots
 * React Query hook for fetching pots with pagination
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { getPots, PotSummaryDto } from '../api/potsApi';
import { useAuthStore } from '../../auth/stores/authStore';
import { potKeys } from './potKeys';

const ITEMS_PER_PAGE = 20;

export interface UseGetPotsReturn {
  pots: PotSummaryDto[];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  loadMore: () => void;
}

/**
 * Hook to fetch pots for a specific pot period with search and pagination
 *
 * @param potPeriodId - The pot period ID to filter by
 * @param debouncedSearch - Pre-debounced search text (debouncing handled by caller)
 */
export function useGetPots(
  potPeriodId: string | null,
  debouncedSearch: string
): UseGetPotsReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  // Fetch pots with infinite query
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: potKeys.list(potPeriodId ?? '', debouncedSearch),
    queryFn: async ({ pageParam = 1 }) => {
      if (!potPeriodId) {
        throw new Error('No pot period selected');
      }

      const result = await getPots({
        potPeriodId,
        search: debouncedSearch || undefined,
        page: pageParam,
        pageSize: ITEMS_PER_PAGE,
      });

      return result;
    },
    getNextPageParam: lastPage => {
      // Return next page number if there are more items
      if (!lastPage) return undefined;
      return lastPage.hasMore ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: isAuthenticated && !!potPeriodId,
    staleTime: 30000, // 30 seconds
  });

  // Flatten pages into single array
  const pots = data?.pages.flatMap(page => page.items) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    pots,
    totalCount,
    hasMore: hasNextPage ?? false,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    error: error as Error | null,
    loadMore: fetchNextPage,
  };
}
